import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CoreModule } from '../core.module';
import { CITY, DEFAULT_CREDIT_PIRCE, POSTALCODE, SEO, STREET, SUPPORT_EMAIL } from '../constants';
import { FAQService } from './faq.service';
import type { NewsItem } from 'src/app/feature/landing/news/news';

@Injectable({
  providedIn: CoreModule,
})
export class SeoService {
  public structuredData: BehaviorSubject<any> = new BehaviorSubject([]);
  private organisationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Mister KLIC',
    legalName: 'Mister KLIC',
    url: 'https://misterklic.nl',
    image: 'https://misterklic.nl/assets/icons/icon-512x512.png',
    logo: 'https://misterklic.nl/assets/icons/icon-512x512.png',
    description: SEO.DEFAULT_DESCRIPTION,
    foundingDate: 2016,
    address: {
      '@type': 'PostalAddress',
      streetAddress: STREET,
      addressLocality: CITY,
      addressRegion: 'Utrecht',
      postalCode: POSTALCODE,
      addressCountry: {
        '@type': 'Country',
        name: 'Nederland',
      },
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: SUPPORT_EMAIL,
    },
  };
  private websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Mister KLIC',
    url: 'https://misterklic.nl/',
    publisher: this.organisationStructuredData,
  };
  constructor(
    private titleService: Title,
    private metaService: Meta,
    private router: Router,
    private faqService: FAQService,
    @Inject(DOCUMENT) private document: Document
  ) {}
  public addSeoData(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      let root = this.router.routerState.snapshot.root;
      // Always set default description, but override it if another description exists.
      this.setDefaults();
      while (root) {
        if (root.children && root.children.length) {
          root = root.children[0];
        } else if (root.data && root.data['title']) {
          this.setTags(root);
          this.setStructuredData(root);
          return;
        } else {
          return;
        }
      }
    });
  }
  public setStructuredData(root: ActivatedRouteSnapshot) {
    const structuredData: object[] = [this.websiteStructuredData, this.organisationStructuredData];
    const type = root.data['structuredData']?.['@type'];

    if (type === 'FAQPage') {
      structuredData.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: this.faqService.faqContentItems.map((faq) => this.setFaqData(faq)),
        ...this.setBasicData(),
      });
    }
    if (type === 'SoftwareApplication') {
      structuredData.push({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        ...this.setBasicData(),
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'EUR',
          // Price per year for 25 to 150 credits, the range of the slider in the pricing table.
          lowPrice: 25 * DEFAULT_CREDIT_PIRCE,
          highPrice: 150 * DEFAULT_CREDIT_PIRCE,
        },
      });
    }
    if (type === 'NewsArticle') {
      const newsItem: NewsItem = root.data['newsItem'];
      structuredData.push({
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        ...this.setBasicData(),
        headline: newsItem.title,
        image: `${environment.frontendUrl}/assets/nieuws/${newsItem.image}`,
        datePublished: newsItem.published,
        dateModified: newsItem.updated,
        author: this.organisationStructuredData,
      });
    }
    // Headline, date and author come from the route's structuredData.
    if (type === 'Article') {
      structuredData.push({
        '@context': 'https://schema.org',
        ...root.data['structuredData'],
        ...this.setBasicData(),
        publisher: this.organisationStructuredData,
      });
    }
    if (this.currentPath() !== '/') {
      structuredData.push(this.setBreadcrumbData());
    }

    this.structuredData.next(structuredData);
  }
  private setBasicData() {
    return {
      name: this.titleService.getTitle(),
      description: this.metaService.getTag('name=description')!.content,
      url: `${environment.frontendUrl}${this.currentPath()}`,
    };
  }
  private setBreadcrumbData() {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${environment.frontendUrl}/` },
        {
          '@type': 'ListItem',
          position: 2,
          name: this.titleService.getTitle().replace(/\s*\|\s*Mister KLIC\s*$/, ''),
          item: `${environment.frontendUrl}${this.currentPath()}`,
        },
      ],
    };
  }
  private currentPath() {
    return this.router.url.split(/[?#]/)[0];
  }
  private setFaqData(faqQuestion: any) {
    return {
      '@type': 'Question',
      name: faqQuestion.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faqQuestion.answer
          .replace(/<[^>]*>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim(),
      },
    };
  }

  private setDefaults() {
    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ property: 'og:site_name', content: 'Mister KLIC' });
    this.metaService.updateTag({ property: 'og:locale', content: 'nl_NL' });
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.setTitle(SEO.DEFAULT_TITLE);
    this.updateTag('description', SEO.DEFAULT_DESCRIPTION);
  }

  public setTags(root: ActivatedRouteSnapshot) {
    this.setTitle(root.data['title']);
    const tags = root.data['metatags'];

    this.removeImageTag();
    this.setImageTag(SEO.DEFAULT_IMAGE);

    if (tags) {
      Object.entries(tags).forEach(([tag, value]: [string, any]) => {
        this.updateTag(tag, value);
      });
    }
  }
  public updateTag(tagName: string, content: string) {
    if (tagName === 'image') {
      this.removeImageTag();
      this.setImageTag(content);
      return;
    }
    this.metaService.removeTag(`name='${tagName}'`);
    this.metaService.addTag({ name: tagName, content: content });
    if (tagName === 'description') {
      this.metaService.updateTag({ property: 'og:description', content: content });
    }
  }
  public setTitle(title: string) {
    this.titleService.setTitle(title);
    this.metaService.updateTag({ property: 'og:title', content: title.trim() });
  }

  private setImageTag(path: string) {
    // og:image must be absolute; image paths in route data are relative to the site root.
    const url = `${environment.frontendUrl}/${path}`;
    let link: HTMLLinkElement = this.document.createElement('link');
    link.setAttribute('rel', 'image_src');
    link.setAttribute('href', url);
    this.document.head.appendChild(link);
    this.metaService.addTag({ property: `og:image`, content: url });
  }

  private removeImageTag() {
    const existingElement = this.document.head.querySelector(`link[rel="image_src"]`);
    if (existingElement) {
      this.document.head.removeChild(existingElement);
    }
    this.metaService.removeTag(`property='og:image'`);
  }
}
